import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { BehaviorSubject, combineLatest, Observable, Subscriber } from 'rxjs';

import { TrialInsightsState } from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';

import { getSiteCostSummaryQuery, SortOrder } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { Utils } from '@shared/utils/utils';

import { TrialInsightsClinicalSiteCostChartService } from './site-cost-chart.service';
import { TrialInsightsClinicalSiteCostTableService } from './site-cost-table.service';
import {
  GenericTrialInsightsQuery,
  SortOrderDefault,
} from '../../classes/trial-insights-query.class';
import {
  TrialInsightsSiteCostKey,
  TrialInsightsTableOptions,
} from '../../models/trial-insights-table.model';

type Results = [State['data'], TrialInsightsSiteCostKey, SortOrder];
type RowData = TrialInsightsTableOptions['rowData']['data'];
type State = Partial<TrialInsightsState['siteCost']>;

const sortDefaults: SortOrderDefault[] = [
  {
    buttonKey: TrialInsightsSiteCostKey.SPEND,
    defaultOrder: SortOrder.DESC,
  },
  {
    buttonKey: TrialInsightsSiteCostKey.LAG,
    defaultOrder: SortOrder.ASC,
  },
];

@Injectable()
export class TrialInsightsClinicalSiteCostQueryService extends GenericTrialInsightsQuery {
  chartService: TrialInsightsClinicalSiteCostChartService;
  tableService: TrialInsightsClinicalSiteCostTableService;
  trialInsightsQuery: TrialInsightsQuery;

  constructor() {
    const trialInsightsQuery = inject(TrialInsightsQuery);
    const tableService = inject(TrialInsightsClinicalSiteCostTableService);
    const chartService = inject(TrialInsightsClinicalSiteCostChartService);

    super({
      chartService,
      slice: 'siteCost',
      sortDefaults,
      tableService,
      trialInsightsQuery,
    });

    this.trialInsightsQuery = trialInsightsQuery;
    this.tableService = tableService;
    this.chartService = chartService;
  }

  processResponseData() {
    const processFn = (sourceValue: Results, subscriber: Subscriber<Results>) => {
      const [data, selectedKey, sortOrder] = sourceValue;

      if (!data || !data.length) {
        this.resetState();

        return;
      }

      const parsedData = this.parseStoreData(data);
      const costData = this.parseStoreDetail(parsedData, selectedKey, sortOrder);

      if (!costData) {
        return;
      }

      // Costs
      const costs =
        selectedKey === TrialInsightsSiteCostKey.SPEND ? costData.site_costs : costData.sites_lag;

      // Sites Activated
      const sitesActivated = costData.sites_activated;
      const siteString = 'Enrolled Site';
      const totalSitesString = sitesActivated === 1 ? siteString : `${siteString}s`;

      this.totalAmount.next(`${sitesActivated} ${totalSitesString}`);

      // Legend Options
      const forecastActivated = costData.expected_sites_activated;
      const forecastClosed = costData.expected_sites_closed;

      const legendOptions = this.chartService.createLegend(forecastActivated, forecastClosed);

      this.legendOptions.next(legendOptions);

      const rowData: RowData = costs.map((row) => {
        let rightHeader = '';
        let rightSubheader = '';

        if ('total_cost' in row) {
          rightHeader = Utils.currencyFormatter(row.total_cost);
          rightSubheader = `${row.patients_enrolled} Patients`;
        } else {
          const date = dayjs(row.latest_transaction);

          if (date.isValid()) {
            rightHeader = date.format('MMM DD, YYYY');
          }
        }

        return {
          buttonKey: selectedKey,
          leftHeader: row.site_name || '',
          leftSubheader:
            row.primary_investigator?.given_name && row.primary_investigator?.family_name
              ? `${row.primary_investigator?.given_name} ${row.primary_investigator?.family_name}`
              : '',
          rightHeader,
          rightSubheader,
        };
      });

      // Table Options
      const tableOptions = this.tableService.createTable(selectedKey, rowData);

      this.tableOptions.next(tableOptions);

      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  processSiteCostSummary$(): Observable<Results> {
    return combineLatest([
      this.data as Observable<getSiteCostSummaryQuery[]>,
      this.selectedKey as BehaviorSubject<TrialInsightsSiteCostKey>,
      this.sortOrder,
    ]).pipe(this.processResponseData());
  }
}
